import { assertNever } from './assertNever'
import type { AvailabilityRuleOccurrence } from './AvailabilityRule'
import { AvailabilityRule, AvailabilityRuleIntersectionType } from './AvailabilityRule'
import { createSortHandler } from './createSortHandler'

export class AvailabilityRuleManager {
  public rangeStart: moment.Moment
  public rangeEnd: moment.Moment
  public rules: AvailabilityRule[]
  public conflictResolver: UncertainConflictResolver = throwingConflictResolver

  constructor(
    rangeStart: moment.Moment,
    rangeEnd: moment.Moment,
    rules: AvailabilityRule[],
    conflictResolver: UncertainConflictResolver = throwingConflictResolver
  ) {
    this.rangeStart = rangeStart
    this.rangeEnd = rangeEnd
    this.rules = rules
    this.conflictResolver = conflictResolver
  }

  async addRule(newRule: AvailabilityRule) {
    const conflicts = this.findConflicts(newRule, this.rules)

    if (!conflicts) {
      this.rules.push(newRule)
    } else {
      let newRules = [newRule]
      for (const conflict of conflicts) {
        const rule = conflict.rule
        const resolved = await this.baseConflictResolver(this.conflictResolver)(conflict)
        if (resolved.action === 'throw') {
          const err = new AvailabilityRuleManagerConflictError(`Conflict between rules: ${rule.toString()} and ${newRule.toString()} - ${resolved.type}`)
          err.situation = conflict.situation
          err.intersection = conflict.intersection
          err.intersectionType = conflict.intersectionType
          throw err
        }

        switch (resolved.type) {
          case 'single-repeating':
            switch (resolved.action) {
              case 'insert': {
                this.removeOccurrence(rule.id, conflict.intersection[0].start)
                break
              }
              case 'discard':
                newRules = []
                break
              default:
                assertNever(resolved.action)
            }
            break
          case 'single-single':
            switch (resolved.action) {
              case 'overwrite': {
                this.removeRule(rule.id)
                break
              }
              default:
                assertNever(resolved)
            }
            break
          case 'repeating-single':
            switch (resolved.action) {
              case 'overwrite': {
                this.removeRule(rule.id)
                break
              }
              case 'get_around': {
                const rules = newRules.flatMap(r => {
                  try {
                    return this.removeOccurrenceFromRule(r, conflict.intersection[1].start)
                  } catch (e) {
                    if (e instanceof AvailabilityRuleManagerUnknownOccurrenceError) {
                      return [r]
                    }
                    throw e
                  }
                })
                newRules = rules
                break
              }
              case 'discard':
                newRules = []
                break
              default:
                assertNever(resolved.action)
            }
            break
          case 'repeating-repeating':
            switch (resolved.action) {
              case 'overwrite': {
                if (newRule.start.isSameOrAfter(rule.start)) {
                  if (conflict.intersectionType === AvailabilityRuleIntersectionType.Precise) {
                    newRules = []
                    break
                  }
                  this.addUntil(rule.id, newRule.start)
                } else {
                  this.removeRule(rule.id)
                }
                break
              }
              default:
                assertNever(resolved)
            }
            break
          default:
            assertNever(resolved)
        }
      }

      for (const newRule of newRules) {
        this.addRuleSync(newRule)
      }
    }

    this.sortRules()
  }

  removeRule(id: string) {
    this.rules = this.rules.filter(r => r.id !== id)
  }

  async updateRule(id: string, newRule: AvailabilityRule) {
    const orule = this.rules.find(r => r.id === id)
    if (!orule) throw new Error(`Unknown rule id: ${id}`)
    const otherRules = this.rules.filter(r => r.id !== id)
    const conflicts = this.findConflicts(newRule, otherRules)

    if (!conflicts) {
      this.rules = this.rules.map(r => (r.id === id ? newRule : r))
      this.sortRules()
    } else {
      this.removeRule(orule.id)
      await this.addRule(newRule)
    }
  }

  removeOccurrence(id: string, date: moment.Moment) {
    const rule = this.rules.find(r => r.id === id)
    if (!rule) throw new Error(`Unknown rule id: ${id}`)

    const rules = this.removeOccurrenceFromRule(rule, date)
    this.removeRule(id)
    for (const rule of rules) {
      this.addRuleSync(rule)
    }

    this.sortRules()
  }

  /** Adds until to the start of the first occurrence that end just before the date */
  addUntil(id: string, date: moment.Moment) {
    const rule = this.rules.find(r => r.id === id)
    if (!rule) throw new Error(`Unknown rule id: ${id}`)

    const occurrences = rule.occurrencesBetween(date.clone().subtract(1, 'month'), date.clone().add(1, 'month'))
    const occurrenceIndex = occurrences.findLastIndex(o => o.start.isSameOrBefore(date))
    this.removeRule(id)

    if (occurrenceIndex !== -1) {
      const occurrence = occurrences[occurrenceIndex]
      const newRule = rule.update({ until: occurrence.start.clone().subtract(1, 'second') })
      this.addRuleSync(newRule)
    }

    this.sortRules()
  }
  /** Adds literal exclusive until ( < date ) to the rule */
  addLiteralUntil(id: string, date: moment.Moment) {
    const rule = this.rules.find(r => r.id === id)
    if (!rule) throw new Error(`Unknown rule id: ${id}`)

    const newRule = rule.update({ until: date.clone().subtract(1, 'second') })
    this.removeRule(id)
    if (!newRule.isEmpty(this.rangeStart, this.rangeEnd)) {
      this.addRuleSync(newRule)
    }
    this.sortRules()
  }

  private findConflicts(newRule: AvailabilityRule, rules: AvailabilityRule[]) {
    const conflicts: AvailabilityRuleManagerConflict[] = []
    for (const rule of rules) {
      const conflict = rule.id === newRule.id ? null : this.rulesIntersection(rule, newRule)

      if (conflict) conflicts.push(conflict)
    }

    if (!conflicts.length) return null
    if (conflicts.length > 1) {
      if (conflicts.every(c => c.situation === 'repeating-single' || c.situation === 'repeating-repeating')) return conflicts

      const err = new AvailabilityRuleManagerMutipleConflictsError('Unsolvable conflicts')
      err.conflicts = conflicts
      throw err
    }

    return conflicts
  }

  private baseConflictResolver = (resolver: UncertainConflictResolver): ConflictResolver => {
    return async conflict => {
      if (conflict.situation === 'repeating-repeating') {
        return { type: 'repeating-repeating', action: 'overwrite' }
      }
      if (conflict.situation === 'single-single') {
        return { type: 'single-single', action: 'overwrite' }
      }
      if (conflict.situation === 'single-repeating' && conflict.intersectionType === AvailabilityRuleIntersectionType.Precise) {
        return { type: 'single-repeating', action: 'discard' }
      }
      if (
        conflict.situation === 'repeating-single' &&
        (conflict.intersectionType === AvailabilityRuleIntersectionType.Precise || conflict.intersectionType === AvailabilityRuleIntersectionType.Cover)
      ) {
        return { type: 'repeating-single', action: 'overwrite' }
      }
      if (conflict.situation === 'repeating-single' && conflict.rule.start.isSame(conflict.newRule.start, 'day')) {
        return { type: 'repeating-single', action: 'overwrite' }
      }
      return resolver(conflict as AvailabilityRuleManagerUncertainConflict)
    }
  }

  private addRuleSync(newRule: AvailabilityRule) {
    for (const rule of this.rules) {
      const conflict = rule.id === newRule.id ? null : this.rulesIntersection(rule, newRule)

      if (conflict) {
        const err = new AvailabilityRuleManagerConflictError(`Conflict between rules: ${rule.toString()} and ${newRule.toString()} - ${conflict.situation}`)
        err.situation = conflict.situation
        err.intersection = conflict.intersection
        err.intersectionType = conflict.intersectionType
        throw err
      }
    }

    this.rules.push(newRule)
    this.sortRules()
  }

  private removeOccurrenceFromRule(rule: AvailabilityRule, date: moment.Moment) {
    const occurrences = rule.occurrencesBetween(date.clone().subtract(1, 'month'), date.clone().add(1, 'month'))
    const occurrenceIndex = occurrences.findIndex(o => o.start.isSame(date))
    const occurrence = occurrenceIndex !== -1 ? occurrences[occurrenceIndex] : null
    if (!occurrence) throw new AvailabilityRuleManagerUnknownOccurrenceError(`Unknown occurrence for date ${date.format()}`)

    const result: AvailabilityRule[] = []

    const newRule = (() => {
      if (occurrenceIndex === 0) return undefined
      const newRule = rule.update({ until: occurrence.start.clone().subtract(1, 'second'), repeats: true })
      if (newRule.isEmpty(this.rangeStart, this.rangeEnd)) return undefined
      return newRule
    })()
    if (newRule) result.push(newRule)

    const secondRule = (() => {
      if (occurrenceIndex === occurrences.length - 1) return undefined
      const newRule = new AvailabilityRule({
        start: occurrence.start.clone().add(1, 'week'),
        end: occurrence.end.clone().add(1, 'week'),
        repeats: true,
        until: rule.until,
      })
      if (newRule.isEmpty(this.rangeStart, this.rangeEnd)) return undefined
      return newRule
    })()

    if (secondRule) result.push(secondRule)
    return result
  }

  private sortRules() {
    this.rules.sort(createSortHandler(r => [-r.start.unix(), r.repeats ? 0 : 1]))
  }

  private rulesIntersection(ruleA: AvailabilityRule, ruleB: AvailabilityRule): AvailabilityRuleManagerConflict | undefined {
    const intersection = AvailabilityRule.firstIntersection([this.rangeStart, this.rangeEnd], ruleA, ruleB)
    if (!intersection) return undefined
    const intersectionType = AvailabilityRule.getIntersectionType(intersection[0], intersection[1])

    switch (true) {
      case ruleA.repeats && ruleB.repeats:
        return {
          situation: 'repeating-repeating',
          rule: ruleA,
          newRule: ruleB,
          intersection,
          intersectionType,
        }
      case !ruleA.repeats && !ruleB.repeats:
        return {
          situation: 'single-single',
          rule: ruleA,
          newRule: ruleB,
          intersection,
          intersectionType,
        }
      case ruleA.repeats && !ruleB.repeats:
        return {
          situation: 'single-repeating',
          rule: ruleA,
          newRule: ruleB,
          intersection,
          intersectionType,
        }
      case !ruleA.repeats && ruleB.repeats:
        return {
          situation: 'repeating-single',
          rule: ruleA,
          newRule: ruleB,
          intersection,
          intersectionType,
        }
      case true:
      default:
        throw new Error('Unknown case occured in rulesIntersection')
    }
  }
}

/**
 * type should be read as "* over *",
 * so "single-repeating" means "single over repeating"
 */
type ConflictType =
  | {
      type: 'single-repeating'
      action:
        | 'throw'
        | 'discard'
        /** excludes date from repeating event and inserts single event */
        | 'insert'
    }
  | {
      type: 'single-single'
      action: /** excludes date from repeating event and inserts single event */ 'overwrite'
    }
  | {
      type: 'repeating-single'
      action:
        | 'throw'
        | 'discard'
        /** makes a hole in a repeating event and gets around a single event */
        | 'get_around'
        /** removes single event */
        | 'overwrite'
    }
  | {
      type: 'repeating-repeating'
      action: /** overwrites previous repeating event if its dtstart is after the new one, or trims */ 'overwrite'
    }

type UncertainConflictType = Exclude<ConflictType, { type: 'repeating-repeating' } | { type: 'single-single' }>

type ConflictTypeLabel = ConflictType['type']
type UncertainConflictTypeLabel = UncertainConflictType['type']

export type AvailabilityRuleManagerConflict = {
  rule: AvailabilityRule
  newRule: AvailabilityRule
  situation: ConflictTypeLabel
  intersection: [AvailabilityRuleOccurrence, AvailabilityRuleOccurrence]
  intersectionType: AvailabilityRuleIntersectionType
}

export type AvailabilityRuleManagerUncertainConflict = {
  rule: AvailabilityRule
  newRule: AvailabilityRule
  situation: UncertainConflictTypeLabel
  intersection: [AvailabilityRuleOccurrence, AvailabilityRuleOccurrence]
  intersectionType: AvailabilityRuleIntersectionType
}

type ConflictResolver = (conflict: AvailabilityRuleManagerConflict) => Promise<ConflictType>

export type UncertainConflictResolver = (conflict: AvailabilityRuleManagerUncertainConflict) => Promise<UncertainConflictType>

const throwingConflictResolver: UncertainConflictResolver = async ({ situation }) => ({ type: situation, action: 'throw' })

class AvailabilityRuleManagerUnknownOccurrenceError extends Error {}

export class AvailabilityRuleManagerMutipleConflictsError extends Error {
  conflicts: AvailabilityRuleManagerConflict[] = []
}

export class AvailabilityRuleManagerConflictError extends Error {
  situation!: ConflictTypeLabel
  intersection!: [AvailabilityRuleOccurrence, AvailabilityRuleOccurrence]
  intersectionType!: AvailabilityRuleIntersectionType
}
